import { inferCategoryFromMatterType } from '../caseTaxonomyRegistry';
import type { DraftingCommandEntry } from './types';

export type DraftingCaseContext = {
  matter_category: string;
  matter_type: string;
  case_type: string;
  side: string;
  primary_relief: string;
  reliefs: string;
};

export function normalizeTaxonomy(text: string | undefined | null): string {
  return (text ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Tokenize taxonomy labels without substring traps (e.g. civil ⊂ criminal). */
export function taxonomyTokens(value: string): Set<string> {
  const v = normalizeTaxonomy(value);
  if (!v) return new Set();
  const tokens = new Set<string>([v]);
  for (const part of v.split(/[/|,]/)) {
    const p = part.trim();
    if (p) tokens.add(p);
  }
  for (const word of v.split(/\s+/)) {
    if (word.length >= 3) tokens.add(word);
  }
  return tokens;
}

export function taxonomyOverlap(caseValues: string[], filterValues: string[]): boolean {
  const caseTokens = new Set<string>();
  for (const cv of caseValues) {
    if (cv) taxonomyTokens(cv).forEach((t) => caseTokens.add(t));
  }
  const filterTokens = new Set<string>();
  for (const fv of filterValues) {
    if (fv) taxonomyTokens(fv).forEach((t) => filterTokens.add(t));
  }
  if (!caseTokens.size || !filterTokens.size) return false;
  for (const t of caseTokens) {
    if (filterTokens.has(t)) return true;
  }
  return false;
}

export function draftingContextFromCase(
  caseData: Record<string, unknown> | null | undefined,
): DraftingCaseContext {
  if (!caseData) {
    return {
      matter_category: '',
      matter_type: '',
      case_type: '',
      side: '',
      primary_relief: '',
      reliefs: '',
    };
  }

  const matterRaw =
    String(caseData.matterType ?? caseData.caseType ?? caseData.caseMatter ?? '');
  const categoryRaw = String(
    caseData.category ?? caseData.caseCategory ?? '',
  );
  const inferred = matterRaw ? inferCategoryFromMatterType(matterRaw) : undefined;

  return {
    matter_category: normalizeTaxonomy(categoryRaw || inferred || ''),
    matter_type: normalizeTaxonomy(matterRaw),
    case_type: normalizeTaxonomy(
      String(caseData.caseType ?? caseData.caseMatter ?? matterRaw),
    ),
    side: normalizeTaxonomy(String(caseData.representingSide ?? '')),
    primary_relief: normalizeTaxonomy(String(caseData.primaryRelief ?? '')),
    reliefs: Array.isArray(caseData.reliefsClaimed)
      ? (caseData.reliefsClaimed as string[]).map(normalizeTaxonomy).join(' ')
      : '',
  };
}

export function caseTaxonomyValues(ctx: DraftingCaseContext): string[] {
  return [ctx.matter_category, ctx.matter_type, ctx.case_type].filter(Boolean);
}

export function hasLinkedCaseTaxonomy(ctx: DraftingCaseContext): boolean {
  return Boolean(ctx.matter_category || ctx.case_type || ctx.matter_type);
}

/** Slash + resolve: permissive when filters empty; strict token overlap when set. */
export function matchesCommandFilters(
  cmd: DraftingCommandEntry,
  ctx: DraftingCaseContext,
): boolean {
  const filters = cmd.filters;
  if (!filters || Object.keys(filters).length === 0) return true;

  const matterCats = (filters.matterCategories ?? []).map(normalizeTaxonomy);
  if (matterCats.length && !taxonomyOverlap(caseTaxonomyValues(ctx), matterCats)) {
    return false;
  }

  const caseTypes = (filters.caseTypes ?? []).map(normalizeTaxonomy);
  if (caseTypes.length) {
    const typeVals = [ctx.case_type, ctx.matter_type].filter(Boolean);
    if (!taxonomyOverlap(typeVals, caseTypes)) return false;
  }

  const sides = (filters.sides ?? []).map(normalizeTaxonomy);
  if (sides.length && ctx.side && !sides.includes(ctx.side)) return false;

  return true;
}

/** Pills: only filings for this matter; no catch-all when a case is linked. */
export function matchesSuggestionCommand(
  cmd: DraftingCommandEntry,
  ctx: DraftingCaseContext,
): boolean {
  if (cmd.category !== 'draft_document') return false;
  if (!matchesCommandFilters(cmd, ctx)) return false;

  if (hasLinkedCaseTaxonomy(ctx)) {
    const filters = cmd.filters;
    if (!filters || Object.keys(filters).length === 0) return false;
    const matterCats = (filters.matterCategories ?? []).map(normalizeTaxonomy);
    if (matterCats.length === 0) return false;
  }

  return true;
}

export function filterContextBonus(
  cmd: DraftingCommandEntry,
  ctx: DraftingCaseContext,
): number {
  const filters = cmd.filters;
  if (!filters || Object.keys(filters).length === 0) {
    return hasLinkedCaseTaxonomy(ctx) ? -20 : 0;
  }

  let bonus = 0;
  const matterCats = (filters.matterCategories ?? []).map(normalizeTaxonomy);
  if (matterCats.length && taxonomyOverlap(caseTaxonomyValues(ctx), matterCats)) {
    bonus += 18;
  }
  const caseTypes = (filters.caseTypes ?? []).map(normalizeTaxonomy);
  if (caseTypes.length && taxonomyOverlap([ctx.case_type, ctx.matter_type], caseTypes)) {
    bonus += 24;
  }
  const sides = (filters.sides ?? []).map(normalizeTaxonomy);
  if (sides.length && ctx.side && sides.includes(ctx.side)) bonus += 12;
  return bonus;
}
